import React, { useRef } from "react";
import { useFormik } from "formik";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import vendorService from "../../services/vendors/vendor.service";

interface AddVendorProps {
  onSuccess?: () => void;
}

const allowOnlyNumbers = (value: string) => value.replace(/[^0-9]/g, '');
const allowOnlyLettersAndSpaces = (value: string) => value.replace(/[^a-zA-Z\s]/g, '');
const AddVendor: React.FC<AddVendorProps> = ({ onSuccess }) => {
  const toast = useRef<Toast>(null);

  const formik = useFormik({
    initialValues: {
      firmInfo: "",
      additionalInfo: "",
      officeContactNbr: "",
      email: "",
      address: "",
      contactPerson1_Name: "",
      contactPerson1_Nbr: "",
      contactPerson2_Name: "",
      contactPerson2_Nbr: "",
      imagePath: ""
    },

    validate: (data) => {
      let errors: any = {};
      if (!data.firmInfo) errors.firmInfo = "Firm name is required";
      if (!data.officeContactNbr) errors.officeContactNbr = "Office contact number is required";
      return errors;
    },

    onSubmit: async (data) => {
      try {
        const response = await vendorService.createVendor(data);
        if (response?.isSuccess) {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Vendor added successfully",
            life: 3000
          });
          formik.resetForm();
          if (onSuccess) onSuccess(); 
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Failed",
            detail: response?.message || "Add failed",
            life: 3000
          });
        }
      } catch (error) {
        console.error("Error adding vendor:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Server error",
          life: 3000
        });
      }
    }
  });

  const getFormErrorMessage = (name: string) =>
    formik.touched[name as keyof typeof formik.touched] &&
    formik.errors[name as keyof typeof formik.errors] ? (
      <small className="p-error">{formik.errors[name as keyof typeof formik.errors]}</small>
    ) : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12 flex align-items-stretch flex-wrap">
        <div className="flex flex-1 bg-white align-items-center justify-content-center">
          <form onSubmit={formik.handleSubmit}>
            <h5 className="border-round mb-3 order-top-color p-2 panel-header-text text-center">
              Add New Vendor
            </h5>
            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <div className="grid">
                  <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="firmInfo">Firm Name<span className="required">*</span></label>
                      <InputText
                        id="firmInfo"
                        name="firmInfo"
                        value={formik.values.firmInfo}
                        onChange={formik.handleChange}
                        autoComplete="off"
                        className={`pl-1 ${classNames({ 'p-invalid': formik.errors.firmInfo })}`}
                      />
                      {getFormErrorMessage("firmInfo")}
                    </div>
                  </div>

                  <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="email">Email</label>
                      <InputText
                        id="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        autoComplete="off"
                        className="pl-1"
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                    <div className="field">
                    <label htmlFor="officeContactNbr">Office Contact No<span className="required">*</span></label>
                    <InputText
                    id="officeContactNbr"
                    name="officeContactNbr"
                    value={formik.values.officeContactNbr}
                    onChange={(e) => 
                    formik.setFieldValue("officeContactNbr", allowOnlyNumbers(e.target.value))
                    }
                    autoComplete="off"
                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.officeContactNbr })}`}
                  />

                      {getFormErrorMessage("officeContactNbr")}
                    </div>
                  </div>

                  <div className="col-12 lg:col-6 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="address">Address</label>
                      <InputTextarea
                        id="address"
                        name="address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        autoComplete="off"
                        className="pl-1"
                        rows={3}
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                    <label htmlFor="contactPerson1_Name">Contact Person 1 Name</label>
                   <InputText
                   id="contactPerson1_Name"
                    name="contactPerson1_Name"
                   value={formik.values.contactPerson1_Name}
                   onChange={(e) =>
                   formik.setFieldValue("contactPerson1_Name", allowOnlyLettersAndSpaces(e.target.value))
                    }
                   autoComplete="off"
                   className="pl-1"
                    />

                  </div>

                 <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
  <label htmlFor="contactPerson1_Nbr">Contact Person 1 Number</label>
  <InputText
    id="contactPerson1_Nbr"
    name="contactPerson1_Nbr"
    value={formik.values.contactPerson1_Nbr}
    onChange={(e) =>
      formik.setFieldValue("contactPerson1_Nbr", allowOnlyNumbers(e.target.value))
    }
    autoComplete="off"
    className="pl-1"
  />
</div>

<div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
  <label htmlFor="contactPerson2_Name">Contact Person 2 Name</label>
  <InputText
    id="contactPerson2_Name"
    name="contactPerson2_Name"
    value={formik.values.contactPerson2_Name}
    onChange={(e) =>
      formik.setFieldValue("contactPerson2_Name", allowOnlyLettersAndSpaces(e.target.value))
    }
    autoComplete="off"
    className="pl-1"
  />
</div>


                 <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
  <label htmlFor="contactPerson2_Nbr">Contact Person 2 Number</label>
  <InputText
    id="contactPerson2_Nbr"
    name="contactPerson2_Nbr"
    value={formik.values.contactPerson2_Nbr}
    onChange={(e) =>
      formik.setFieldValue("contactPerson2_Nbr", allowOnlyNumbers(e.target.value))
    }
    autoComplete="off"
    className="pl-1"
  />
</div>


                  <div className="col-12 lg:col-6 mb-3 px-3">
                    <label htmlFor="additionalInfo">Additional Info</label>
                    <InputTextarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formik.values.additionalInfo}
                      onChange={formik.handleChange}
                      autoComplete="off"
                      rows={3}
                      className="pl-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" label="Add Vendor" className="right-align" />
          </form>
        </div>
      </div>
    </>
  );
};

export default AddVendor;
